/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import CrudEdit from '~/components/CrudEdit'
import IInspectionConfig from '../interfaces/IInspectionConfig'
import { useHookstate } from '@hookstate/core'
import { authFetch } from '~/services/fetch'
import { useParams } from 'react-router-dom'
import { Checkbox } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid'
import { useLang } from '~/hooks/useLang'

interface IInspectionItemLine {
  readonly id: number
  readonly name: string
  readonly required: boolean
}

export default function Edit() {
  const { id } = useParams<{ readonly id: string }>()
  const {
    register,
    handleSubmit,
    control,
    setValue: setFormValue,
    getValues,
    watch,
    resetField,
  } = useForm<IInspectionConfig>({})

  const { translate } = useLang()
  const rows = useHookstate<readonly IInspectionItemLine[]>([])

  useEffect(() => {
    if (!id) return

    authFetch({
      url: `/inspection-config/${id}`,
      method: 'GET',
    }).then((response) => {
      const data = response.data
      // Seta todos os valores do form
      setFormValue('id', data.id)
      setFormValue('device_type_id', data.device_type?.id)
      setFormValue('agency_id', data.agency?.id)

      // Formata o item para a tabela
      const formattedItem = {
        id: data.inspection_item?.id ?? 0,
        name: data.inspection_item?.name ?? '',
        required: data.required === 1,
      }
            
      // Seta na tabela (para visualização)
      rows.set([formattedItem])
      
      // Seta no form (para envio)
      setFormValue('inspection_items', [{
        id: formattedItem.id,
        required: data.required
      }])
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleCheckboxChange = (rowId: number, checked: boolean) => {
    const updatedRows = rows.get().map(currentRow => 
      currentRow.id === rowId ? { ...currentRow, required: checked } : currentRow
    )
    rows.set(updatedRows)
    
    // Atualiza o form
    setFormValue('inspection_items', updatedRows.map(currentRow => ({
      id: currentRow.id,
      required: currentRow.required ? 1 : 0
    })))
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      editable: false,
    },
    {
      field: 'name',
      headerName: translate('Inspection Item'),
      flex: 1,
      editable: false,
    },
    {
      field: 'required',
      headerName: translate('Required'),
      width: 130,
      renderCell: (params: GridRenderCellParams<IInspectionItemLine>) => (
        <Checkbox
          checked={params.value === true}
          onChange={(event) => handleCheckboxChange(params.row.id, event.target.checked)}
        />
      ),
    },
  ]

  const fields = [
    { name: 'id', type: 'hidden' },
    { 
      name: 'device_type_id', 
      type: 'selectAutoComplete',
      model: 'device-type',
      placeholder: translate('Device Type'),
      required: true,
      disabled: true,
    },
    { 
      name: 'agency_id', 
      type: 'selectAutoComplete',
      model: 'agency',
      placeholder: translate('Agency:name'),
      required: true,
      disabled: true,
    },
    {
      name: 'inspection_items',
      type: 'table',
      placeholder: translate('Inspection Items'),
      lockFields: true,
      hideTitle: true,
      columns: columns,
      rows: rows,
    },
  ]

  return (
    <CrudEdit
      model={'inspection-config'}
      fields={fields}
      register={register}
      handleSubmit={handleSubmit}
      control={control}
      setFormValue={setFormValue}
      messageSuccess={translate('Inspection configuration updated successfully!')}
      title={translate('Edit Inspection Configuration')}
      getValues={getValues}
      watch={watch}
      resetField={resetField}
      disableAutoLoad={true}
    />
  )
}
